import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { useToast } from "@/hooks/use-toast";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { UserClaimsList } from "@/components/dashboard/claims/UserClaimsList";
import { ClaimFormContainer } from "@/components/admin/contact-details/sections/claims/ClaimFormContainer";
import DashboardLoading from "@/components/dashboard/DashboardLoading";

const ClaimsPage = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Session error:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to get session. Please try logging in again."
          });
          return;
        }

        if (session?.user) {
          console.log("Current user ID:", session.user.id);
          setUserId(session.user.id);
        } else {
          console.log("No active session found");
          toast({
            variant: "destructive",
            title: "Not Logged In",
            description: "Please log in to view your claims."
          });
        }
      } catch (error) {
        console.error("Error getting session:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred. Please try again."
        });
      } finally {
        setLoading(false);
      }
    };

    getSession();
  }, [toast]);

  const handleClaimSubmitted = () => {
    setRefreshKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <PageLayout>
        <DashboardLoading />
      </PageLayout>
    );
  }

  return (
    <PageLayout showBreadcrumbs={false}>
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <DashboardSidebar />
          <div className="flex-1 p-8">
            <h1 className="text-3xl font-bold mb-8">Claims</h1>
            {userId ? (
              <div className="space-y-8">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="p-6">
                    <h2 className="text-lg font-medium text-primary-dark mb-4">
                      Submit a Claim
                    </h2>
                    <ClaimFormContainer 
                      contactId={userId} 
                      onSubmitSuccess={handleClaimSubmitted}
                    />
                  </div>
                </div>
                <UserClaimsList userId={userId} key={refreshKey} />
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Please sign in to view and submit claims.
              </div>
            )}
          </div>
        </div>
      </SidebarProvider>
    </PageLayout>
  );
};

export default ClaimsPage;